import useConfigValues from '../../ReduxFunctions/useConfigValues';
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchData } from "../../Helpers/externapi";
import { setHospitalImage } from "../../ReduxFunctions/ReduxSlice";
import { rangeOptions } from "../constants/rangeOptions";
import {
  asArrayTop,
  dateKey,
  getVisitCustomerKey,
  normalizeName,
  numberValue,
  toDisplayDate,
} from "../utils/dashboardUtils";

const RANGE_DAY_OFFSETS = {
  "7d": 6,
  "30d": 29,
  "90d": 89,
};

const getVisitDateKey = (visit) => {
  return dateKey(visit.AppointmentDate || visit.BookingDate);
};

const getFixedRangeBounds = (range) => {
  const dayOffset = RANGE_DAY_OFFSETS[range];
  if (dayOffset === undefined) {
    return { fromDate: "", toDate: "" };
  }

  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - dayOffset);

  return {
    fromDate: dateKey(startDate),
    toDate: dateKey(endDate),
  };
};

const getAllTimeBounds = (allVisits) => {
  const visitDateKeys = allVisits.map(getVisitDateKey).filter(Boolean);
  if (visitDateKeys.length === 0) {
    return { fromDate: "", toDate: "" };
  }

  const periodBounds = visitDateKeys.reduce(
    (bounds, currentDate) => ({
      minDate:
        currentDate.localeCompare(bounds.minDate) < 0
          ? currentDate
          : bounds.minDate,
      maxDate:
        currentDate.localeCompare(bounds.maxDate) > 0
          ? currentDate
          : bounds.maxDate,
    }),
    { minDate: visitDateKeys[0], maxDate: visitDateKeys[0] },
  );

  return {
    fromDate: periodBounds.minDate,
    toDate: periodBounds.maxDate,
  };
};

const useHospitalDashboardController = () => {
  const configValues = useConfigValues('hospitalImagesURL');
  const hospitalImage = useSelector((state) => state.hospitalImage);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const hospitalId = sessionStorage.getItem("hospitalId");
  const hospitalName = sessionStorage.getItem("hospitalName") || "Hospital";
  const hospitalLogo = sessionStorage.getItem("hospitalImage");

  const [allVisits, setAllVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [range, setRange] = useState("30d");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [customerVisitSearch, setCustomerVisitSearch] = useState("");

  useEffect(() => {
    if (!configValues.length) {
      return;
    }

    if (!hospitalImage.length) {
      const imageUrl = configValues.find(
        (val) => val.ConfigKey === "hospitalImagesURL",
      );
      if (imageUrl && hospitalLogo) {
        dispatch(setHospitalImage(imageUrl.ConfigValue + hospitalLogo));
      }
    }
  }, [configValues, dispatch, hospitalImage, hospitalLogo]);

  useEffect(() => {
    const fetchVisits = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const response = await fetchData(
          "BookingConsultation/ConsultationListByHospitalId",
          {
            skip: 0,
            take: 0,
            HospitalId: hospitalId,
          },
        );

        if (!response?.data) {
          setAllVisits([]);
          setErrorMessage("No data returned from server.");
          return;
        }

        setAllVisits(response.data);
      } catch (error) {
        console.error("Error fetching dashboard insights:", error);
        setErrorMessage("Unable to fetch dashboard insights right now.");
      } finally {
        setLoading(false);
      }
    };

    if (hospitalId) {
      fetchVisits();
    }
  }, [hospitalId]);

  useEffect(() => {
    if (range === "custom") {
      return;
    }

    if (range === "all") {
      const bounds = getAllTimeBounds(allVisits);
      setFromDate(bounds.fromDate);
      setToDate(bounds.toDate);
      return;
    }

    const bounds = getFixedRangeBounds(range);
    setFromDate(bounds.fromDate);
    setToDate(bounds.toDate);
  }, [range, allVisits]);

  const filteredVisits = useMemo(() => {
    if (!allVisits.length) {
      return [];
    }

    if (range === "all") {
      return allVisits;
    }

    const lowerDateKey = getFixedRangeBounds(range).fromDate;

    return allVisits.filter((visit) => {
      const visitDateKey = getVisitDateKey(visit);
      if (!visitDateKey) {
        return false;
      }

      if (range === "custom") {
        if (!fromDate || !toDate) {
          return true;
        }
        return visitDateKey >= fromDate && visitDateKey <= toDate;
      }

      return visitDateKey >= lowerDateKey;
    });
  }, [allVisits, range, fromDate, toDate]);

  const insights = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);

    const visitDateCountMap = filteredVisits.reduce((mapObj, visit) => {
      const visitDate = getVisitDateKey(visit);
      if (!visitDate) {
        return mapObj;
      }

      mapObj[visitDate] = (mapObj[visitDate] || 0) + 1;
      return mapObj;
    }, {});

    const totalVisits = filteredVisits.length;
    const todayVisits = visitDateCountMap[today] || 0;

    const uniquePatients = new Set(
      filteredVisits
        .map(
          (visit) =>
            visit.CustomerId ||
            visit.MemberDependentId ||
            visit.MemberId ||
            visit.MobileNumber,
        )
        .filter(Boolean),
    ).size;

    const couponClaimed = filteredVisits.filter(
      (visit) => Number(visit.IsCouponClaimed) === 1,
    ).length;

    const collectedAmount = filteredVisits.reduce(
      (sum, visit) => sum + numberValue(visit.PaidAmount),
      0,
    );
    const billedAmount = filteredVisits.reduce(
      (sum, visit) => sum + numberValue(visit.TotalAmount),
      0,
    );
    const unpaidAmount = Math.max(billedAmount - collectedAmount, 0);

    const memberVisitCounter = {};
    const genderCounter = {};

    filteredVisits.forEach((visit) => {
      const key = visit.MemberId || visit.CustomerId || visit.MobileNumber;
      if (key) {
        memberVisitCounter[key] = (memberVisitCounter[key] || 0) + 1;
      }

      const gender = normalizeName(visit.Gender, "Unknown");
      genderCounter[gender] = (genderCounter[gender] || 0) + 1;
    });

    const returningPatients = Object.values(memberVisitCounter).filter(
      (count) => count > 1,
    ).length;

    const last7Days = [];
    for (let i = 6; i >= 0; i -= 1) {
      const day = new Date();
      day.setDate(day.getDate() - i);
      const key = day.toISOString().slice(0, 10);
      const shortDay = day.toLocaleDateString("en-US", { weekday: "short" });

      const count = visitDateCountMap[key] || 0;

      last7Days.push({ key, label: shortDay, count });
    }

    const peak7Days = Math.max(...last7Days.map((day) => day.count), 1);

    return {
      totalVisits,
      todayVisits,
      uniquePatients,
      couponClaimed,
      collectedAmount,
      billedAmount,
      unpaidAmount,
      averageBill: totalVisits ? billedAmount / totalVisits : 0,
      revisitRate: uniquePatients
        ? Math.round((returningPatients / uniquePatients) * 100)
        : 0,
      genders: asArrayTop(genderCounter, 6),
      trend7Days: last7Days,
      peak7Days,
    };
  }, [filteredVisits]);

  const selectedRangeLabel = useMemo(() => {
    const selected = rangeOptions.find((option) => option.value === range);

    if (!selected) {
      return "Last 30 days";
    }

    if (range === "custom" && fromDate && toDate) {
      return `${toDisplayDate(fromDate)} to ${toDisplayDate(toDate)}`;
    }

    return selected.label;
  }, [range, fromDate, toDate]);

  const selectedPeriodLabel = useMemo(() => {
    if (fromDate && toDate) {
      return `${toDisplayDate(fromDate)} to ${toDisplayDate(toDate)}`;
    }

    return "All available data";
  }, [fromDate, toDate]);

  const customerVisitRows = useMemo(() => {
    const visitMap = {};

    filteredVisits.forEach((visit) => {
      const key = getVisitCustomerKey(visit);
      if (!key) {
        return;
      }

      if (!visitMap[key]) {
        visitMap[key] = {
          key,
          patientName: normalizeName(visit.Name, "-"),
          mobileNumber: visit.MobileNumber || "-",
          visits: 0,
        };
      }

      visitMap[key].visits += 1;

      if (visitMap[key].patientName === "-" && visit.Name) {
        visitMap[key].patientName = normalizeName(visit.Name, "-");
      }

      if (visitMap[key].mobileNumber === "-" && visit.MobileNumber) {
        visitMap[key].mobileNumber = visit.MobileNumber;
      }
    });

    return Object.values(visitMap)
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 20);
  }, [filteredVisits]);

  const filteredCustomerVisitRows = useMemo(() => {
    const query = customerVisitSearch.trim().toLowerCase();

    if (!query) {
      return customerVisitRows;
    }

    return customerVisitRows.filter((row) => {
      const name = String(row.patientName || "").toLowerCase();
      const mobile = String(row.mobileNumber || "").toLowerCase();

      return name.includes(query) || mobile.includes(query);
    });
  }, [customerVisitRows, customerVisitSearch]);

  const resetFilters = () => {
    setRange("30d");
    setFromDate("");
    setToDate("");
  };

  const goToVerify = () => {
    navigate("/verify");
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  return {
    loading,
    errorMessage,
    hospitalName,
    hospitalImage,
    range,
    fromDate,
    toDate,
    selectedRangeLabel,
    selectedPeriodLabel,
    customerVisitSearch,
    filteredVisits,
    opdVisits: allVisits,
    insights,
    filteredCustomerVisitRows,
    setRange,
    setFromDate,
    setToDate,
    setCustomerVisitSearch,
    resetFilters,
    goToVerify,
    handleLogout,
  };
};

export default useHospitalDashboardController;
